import React from 'react';
import { Box, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Shared arena pager — chevron pills + a mono "01 / 24" indicator (current page
// in cyan). Used by the Digital Collectibles grid and the Marketplace.
const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

const pagerBtnSx = {
  fontWeight: 800,
  fontSize: '0.95rem',
  color: '#38e8ff',
  backgroundColor: 'rgba(56, 232, 255, 0.12)',
  border: '1px solid rgba(56, 232, 255, 0.4)',
  boxShadow: 'none',
  borderRadius: '10px',
  minHeight: 52,
  px: 3,
  '&:hover': { backgroundColor: 'rgba(56, 232, 255, 0.2)', boxShadow: 'none' },
  '&.Mui-disabled': { opacity: 0.35, color: '#38e8ff' }
} as const;

const pad2 = (n: number) => String(n).padStart(2, '0');

type ArenaPagerProps = {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
};

export const ArenaPager = ({
  page,
  totalPages,
  onPrev,
  onNext
}: ArenaPagerProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
      py: '1rem'
    }}
  >
    <Button
      onClick={onPrev}
      disabled={page === 1}
      variant="contained"
      startIcon={<ChevronLeftIcon />}
      sx={pagerBtnSx}
    >
      Back
    </Button>
    <Box
      sx={{
        minHeight: 52,
        display: 'flex',
        alignItems: 'center',
        px: 2.5,
        borderRadius: '10px',
        border: '1px solid rgba(56, 232, 255, 0.22)',
        background: 'rgba(7, 11, 17, 0.6)',
        font: `800 16px ${MONO}`,
        letterSpacing: '0.12em',
        color: '#8ea3b3',
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      <Box component="span" sx={{ color: '#38e8ff' }}>
        {pad2(page)}
      </Box>
      <Box component="span" sx={{ mx: 0.9, opacity: 0.6 }}>
        /
      </Box>
      {pad2(Math.max(totalPages, 1))}
    </Box>
    <Button
      onClick={onNext}
      disabled={page === totalPages || totalPages === 0}
      variant="contained"
      endIcon={<ChevronRightIcon />}
      sx={pagerBtnSx}
    >
      Next
    </Button>
  </Box>
);
